import { useState, useEffect, useMemo } from 'react';
import { Transaction } from '../../types/organization/finances';
import { fetchTransactions } from '../../services/organization/finances';

export const useTransactions = (organizationUuid: string | null, activeTab: string) => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [selectedEvent, setSelectedEvent] = useState<string>('all');
    const [showEventDropdown, setShowEventDropdown] = useState<boolean>(false);

    useEffect(() => {
        const loadTransactions = async () => {
            if (!organizationUuid) return;
            try {
                setIsLoading(true);
                const data = await fetchTransactions(organizationUuid);
                setTransactions(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
            } finally {
                setIsLoading(false);
            }
        };

        if (activeTab === 'transactions') {
            loadTransactions();
        }
    }, [organizationUuid, activeTab]);

    // Get unique events from transactions
    const uniqueEvents = useMemo(() => {
        const events = new Map<string, string>();
        events.set('all', 'All Events');
        transactions.forEach(txn => {
            if (txn.event) {
                events.set(txn.event.uuid, txn.event.title);
            }
        });
        return Array.from(events.entries());
    }, [transactions]);

    const filteredTransactions = useMemo(() => 
        transactions.filter(txn => 
            (selectedEvent === 'all' || txn.event?.uuid === selectedEvent) &&
            ((txn.ticket?.customer_name || txn.billing_details?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (txn.ticket?.customer_email || txn.billing_details?.email || '').toLowerCase().includes(searchQuery.toLowerCase()))
        ),
        [transactions, selectedEvent, searchQuery]
    );

    return {
        transactions,
        isLoading,
        error,
        searchQuery,
        selectedEvent,
        showEventDropdown,
        uniqueEvents,
        filteredTransactions,
        setSearchQuery,
        setSelectedEvent,
        setShowEventDropdown
    };
}; 